import React from 'react';
import styled from 'styled-components';
import * as XLSX from 'xlsx';
import { getTimeEntriesForExport } from '../../services/supabase';
import { getLastSevenDaysRange, getCurrentMonthRange } from '../../utils/utils';

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.08);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.2rem;
  color: #222;
`;

const ProgressBarContainer = styled.div`
  background: #f2f2f2;
  border-radius: 4px;
  height: 10px;
  margin: 0.5rem 0 1rem 0;
`;

const ProgressBar = styled.div`
  background: var(--primary-green);
  height: 100%;
  border-radius: 4px;
  width: ${({ percent }) => percent}%;
  transition: width 0.3s;
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.2rem 2rem;
  margin: 1.5rem 0 0.5rem 0;
`;

const StatCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-size: 1rem;
  min-width: 0;
`;

const StatLabel = styled.div`
  color: #666;
  font-size: 0.98rem;
  margin-bottom: 0.2rem;
`;

const StatValue = styled.div`
  font-weight: 500;
  font-size: 1.25rem;
  color: #333;
`;

const CurrentWeekLabel = styled.div`
  font-weight: 500;
  font-size: 1rem;
  color: #333;
`;

const CurrentWeekValue = styled.div`
  font-weight: 500;
  font-size: 1rem;
  color: #333;
`;

const { start: weekStart, end: weekEnd, formatted: weekLabel } = getLastSevenDaysRange();


const formatDuration = (hours) => {
  if (!hours || isNaN(hours)) return '0 hr';
  const totalMinutes = Math.round(hours * 60);
  const hrs = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (mins === 0) return `${hrs} hr`;
  if (hrs === 0) return `${mins} min`;
  return `${hrs} hr ${mins} min`;
};

const formatDateForFilename = (date) => {
  return date.toISOString().slice(0, 10); // YYYY-MM-DD
};

const formatLocalDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString(); // Only date
};

const formatLocalTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); // e.g., 01:18 PM
};

const formatLocalDateTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleString(); // Date and time, local format
};

const TimeSummaryCard = React.memo(({
  today = 0,
  yesterday = 0,
  thisWeek = 0,
  thisMonth = 0,
  weekGoal = 40,
  isTracking = false,
  onViewReports,
  userId
}) => {
  const percent = Math.min(100, (thisWeek / weekGoal) * 100);

  const handleExport = async (range) => {
    let from, to, filename;
    const now = new Date();

    if (range === 'week') {
      // Last 7 days
      const { start: monday, end: sunday } = getLastSevenDaysRange();
      from = monday.toISOString();
      to = sunday.toISOString();
      filename = `time_entries_${formatDateForFilename(monday)}_to_${formatDateForFilename(sunday)}.xlsx`;
    } else if (range === 'month') {
      // This month
      const { start: startOfMonth, end: endOfMonth } = getCurrentMonthRange();
      from = startOfMonth.toISOString();
      to = endOfMonth.toISOString();
      filename = `time_entries_${formatDateForFilename(startOfMonth)}_to_${formatDateForFilename(endOfMonth)}.xlsx`;
    } else {
      // All time - don't set from/to
      filename = `time_entries_all.xlsx`;
    }

    const { data, error } = await getTimeEntriesForExport(userId, { from, to });
    if (error) {
      alert('Failed to fetch time entries');
      return;
    }

    // Format data for Excel
    const excelData = data.map(entry => ({
      Date: formatLocalDate(entry.date || entry.created_at),
      'Start Time': formatLocalTime(entry.start_time),
      'End Time': formatLocalTime(entry.end_time),
      Hours: entry.duration_minutes ? (entry.duration_minutes / 60).toFixed(2) : '',
      Category: entry.category,
      Location: entry.location,
      Description: entry.description,
    }));

    // Calculate total hours
    const totalHours = data.reduce((sum, entry) => {
      return sum + (entry.duration_minutes ? entry.duration_minutes / 60 : 0);
    }, 0);

    // Add total row
    excelData.push({
      Date: 'Total',
      'Start Time': '',
      'End Time': '',
      Hours: totalHours.toFixed(2),
      Category: '',
      Location: '',
      Description: '',
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Style the total row
    const lastRow = excelData.length;
    worksheet['!rows'] = worksheet['!rows'] || [];
    worksheet['!rows'][lastRow - 1] = { 
      hpt: 25, // Height in points
      level: 0,
      hidden: false,
      customFormat: true
    };

    // Make total row bold
    const drange = XLSX.utils.decode_range(worksheet['!ref']);
    for (let C = drange.s.c; C <= drange.e.c; ++C) {
      const cellRef = XLSX.utils.encode_cell({ r: lastRow - 1, c: C });
      if (!worksheet[cellRef]) continue;
      worksheet[cellRef].s = {
        font: { bold: true },
        alignment: { horizontal: 'left' }
      };
    }

    const objectMaxLength = [];
    excelData.forEach(row => {
      Object.keys(row).forEach((key, colIdx) => {
        const value = row[key] ? String(row[key]) : '';
        objectMaxLength[colIdx] = Math.max(objectMaxLength[colIdx] || key.length, value.length);
      });
    });
    worksheet['!cols'] = objectMaxLength.map(w => ({ wch: w + 2 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Time Entries');
    XLSX.writeFile(workbook, filename);
  };

  return (
    <Card>
      <SectionTitle>Time Summary</SectionTitle>
      <StatRow>
        <CurrentWeekLabel>Current Week</CurrentWeekLabel>
        <CurrentWeekValue>
          {formatDuration(thisWeek)} / {formatDuration(weekGoal)}
        </CurrentWeekValue>
      </StatRow>
      <ProgressBarContainer>
        <ProgressBar percent={percent} />
      </ProgressBarContainer>
      <div style={{ fontSize: '0.85rem', color: '#888', marginBottom: '1rem' }}>
        {percent.toFixed(2)}% &nbsp; &nbsp; <span style={{ float: 'right' }}>{weekLabel}</span>    
      </div>
      <StatsGrid>
        <StatCell>
          <StatLabel>Today</StatLabel>
          <StatValue>{formatDuration(today)}</StatValue>
        </StatCell>
        <StatCell>
          <StatLabel>Yesterday</StatLabel>
          <StatValue>{formatDuration(yesterday)}</StatValue>
        </StatCell>
        <StatCell>
          <StatLabel>Last 7 Days</StatLabel>
          <StatValue>{formatDuration(thisWeek)}</StatValue>
        </StatCell>
        <StatCell>
          <StatLabel>This Month</StatLabel>
          <StatValue>{formatDuration(thisMonth)}</StatValue>
        </StatCell>
      </StatsGrid>
      {/* <ExportSection>
        <ExportLabel>
          <FileSpreadsheet color="var(--primary-green)" size={18} />
          Export to Excel
        </ExportLabel>
        <ExportButtonGroup>
          <ExportButton onClick={() => handleExport('week')}><Download size={18}/>Last 7 Days</ExportButton>
          <ExportButton onClick={() => handleExport('month')}><Download size={18}/>This Month</ExportButton>
          <ExportButton onClick={() => handleExport('total')}><Download size={18}/>All Time</ExportButton>
        </ExportButtonGroup>
      </ExportSection> */}
    </Card>
  );
});

export default TimeSummaryCard;